"use client";

import { type ReactNode, useEffect, useRef } from "react";
import { Body, Display } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";

export type StepDirection = "forward" | "back";

/**
 * One screen of the application: its question and its fields.
 *
 * Keyed by step in the parent, so each one mounts fresh and enters through
 * `starting:` (`@starting-style`). That is a transition rather than a
 * keyframe, so a Back pressed mid-entrance retargets from wherever the step
 * is instead of snapping to the start. It arrives from the side it is
 * travelling to: forward from the right, back from the left, the way a stack
 * of screens moves, so Back reads as returning rather than as a new screen.
 *
 * No exit. The outgoing step is gone on the frame the next one starts; a
 * short entrance is all the continuity a question-at-a-time form needs, and
 * making someone watch the old question leave is latency on every Continue.
 * Reduced motion keeps the fade and drops the travel.
 */
export function ApplyStep({
  title,
  lede,
  direction,
  takeFocus,
  children,
}: {
  title: string;
  lede: string;
  direction: StepDirection;
  /** False on the first paint, when nothing moved and focus belongs to the page. */
  takeFocus: boolean;
  children: ReactNode;
}) {
  const headerRef = useRef<HTMLElement>(null);

  // A new question is a new page as far as a screen reader is concerned, and
  // the button that moved them here is still focused otherwise, so the next
  // Tab would skip the whole step. The header, not the first field: landing
  // on an input would open the keyboard on a phone before they have read
  // what is being asked.
  useEffect(() => {
    if (!takeFocus) return;
    headerRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div
      className={cn(
        "flex flex-col gap-nf-ds-6",
        "transition-[opacity,translate] duration-nf-ds-3 ease-nf-ds-out starting:opacity-0",
        direction === "forward"
          ? "motion-safe:starting:translate-x-nf-ds-6"
          : "motion-safe:starting:-translate-x-nf-ds-6",
      )}
    >
      <header
        ref={headerRef}
        tabIndex={-1}
        className="flex min-w-0 flex-col items-center gap-nf-ds-2 text-center outline-hidden"
      >
        <Display as="h1" className="text-balance">
          {title}
        </Display>
        <Body muted className="text-pretty">
          {lede}
        </Body>
      </header>
      {children}
    </div>
  );
}
